import { useAuth } from '@/firebase/index';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Loader2, Edit, LogOut, Crown } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import Image from '@/components/ui/Image';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from '../ui/input';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Label } from '../ui/label';
import { updateUserProfile } from '@/lib/actions';
import { auth as mainAuth } from '@/firebase/config';


export function UserProfileCard() {
  const { user, userData, loading, refetch } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [newImageUrl, setNewImageUrl] = useState(userData?.profileImageUrl || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleUpdateProfile = async () => {
    if (!user) return;
    setIsUpdating(true);
    
    let imageUpdateSuccess = true;
    if (newImageUrl && newImageUrl !== userData?.profileImageUrl) {
        if (!newImageUrl.startsWith('https://i.imgur.com/')) {
            toast({
                title: "URL no válida",
                description: "La URL de la imagen debe empezar con 'https://i.imgur.com/'.",
                variant: "destructive",
            });
            imageUpdateSuccess = false;
        } else {
            const result = await updateUserProfile({ profileImageUrl: newImageUrl });
            if (!result.success) {
                imageUpdateSuccess = false;
                toast({ title: "Error", description: result.message || "No se pudo actualizar la imagen de perfil.", variant: "destructive" });
            }
        }
    }
    
    if (imageUpdateSuccess) {
        toast({ title: "¡Éxito!", description: "Tu perfil ha sido actualizado." });
        await refetch();
        setIsDialogOpen(false);
    }

    setIsUpdating(false);
  };


  const handleLogout = async () => {
    await mainAuth.signOut();
    navigate('/login');
  }


  if (loading) {
    return (
      <div className="flex items-center p-4">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null; // or some placeholder
  }

  const nickname = userData?.nickname || user.email?.split('@')[0] || 'Usuario';
  const profileImageUrl = userData?.profileImageUrl || 'https://i.imgur.com/AJ3InNO.png';
  const isAdmin = userData?.role === 'admin';

  return (
    <div className="flex w-full items-center justify-between p-2">
        <div className="flex items-center gap-3 overflow-hidden">
            <div className="relative">
              <Avatar className="h-10 w-10">
                  <AvatarImage src={profileImageUrl} alt={`Foto de perfil de ${nickname}`} />
                  <AvatarFallback>{nickname.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
              {isAdmin && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1 border-2 border-sidebar-background">
                        <Crown className="h-3 w-3 text-primary-foreground" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Administrador</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <div className="flex flex-col truncate group-data-[state=collapsed]/sidebar-wrapper:hidden">
                <span className="text-sm font-semibold truncate text-sidebar-foreground">{nickname}</span>
                <span className="text-xs truncate text-muted-foreground">{user.email}</span>
            </div>
        </div>
        <div className="flex items-center gap-1 group-data-[state=collapsed]/sidebar-wrapper:hidden">
             <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <DialogTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </DialogTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="top">Editar Perfil</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar Perfil</DialogTitle>
                        <DialogDescription>Actualiza tu foto de perfil.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                         <div className="space-y-2">
                             <Label htmlFor="profile-image">Foto de Perfil (URL de Imgur)</Label>
                             <Input
                                id="profile-image"
                                type="url"
                                placeholder="https://i.imgur.com/..."
                                defaultValue={userData?.profileImageUrl || ''}
                                onChange={(e) => setNewImageUrl(e.target.value)}
                            />
                         </div>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline">Cancelar</Button>
                        </DialogClose>
                        <Button onClick={handleUpdateProfile} disabled={isUpdating}>
                            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Guardar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                         <Button onClick={handleLogout} variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Cerrar Sesión</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    </div>
  );
}
